/**
 * The playout view's clock bar (PLAN §7.4): the activation, what is on air
 * and its countdown (an overrun counts up in the on-air colour), what is
 * next, and the show's pace — over/under, and the expected and projected end
 * as times of day per the playlist's timing mode.
 *
 * Every time comes in as a number; formatting is `lib/playout/format.ts`'s.
 */
import { formatCountdown, formatElapsed, formatLength, formatOverUnder, formatTimeOfDay, type OverUnder } from '../../lib/playout/format.js';
import { cn } from '../../lib/utils.js';
import { Badge } from '../ui/badge.js';

export type TimingMode = 'none' | 'forward-time' | 'back-time' | 'duration';

export type PlayoutHeaderProps = {
  readonly productionLabel: string;
  readonly activation: 'inactive' | 'active' | 'rehearsal';
  readonly onAir: { readonly title: string; readonly elapsedMs: number; readonly remainingMs: number | null } | null;
  readonly nextTitle: string | null;
  readonly mode: TimingMode;
  readonly remainingMs: number;
  readonly overUnderMs: number | null;
  readonly expectedEnd: number | null;
  readonly projectedEnd: number | null;
  /** The live stream: `production-lost` is the node's link to the production process down (the socket is fine). */
  readonly connection: 'connecting' | 'live' | 'reconnecting' | 'production-lost';
};

const CONNECTION_LABEL: Readonly<Record<Exclude<PlayoutHeaderProps['connection'], 'live'>, string>> = {
  connecting: 'Connecting…',
  reconnecting: 'Reconnecting…',
  'production-lost': 'Reconnecting to the production…',
};

const MODE_LABEL: Readonly<Record<TimingMode, string>> = {
  none: 'No timing',
  'forward-time': 'Forward time',
  'back-time': 'Back time',
  duration: 'Duration',
};

/** Over is the on-air colour, under the next colour — as pills, so the text keeps the pair's tested contrast. */
const TONE_CLASS: Readonly<Record<OverUnder['tone'], string>> = {
  over: 'rounded bg-onair px-1 text-onair-foreground',
  under: 'rounded bg-next px-1 text-next-foreground',
  even: 'text-foreground',
  none: 'text-muted-foreground',
};

function Stat({ label, value, testId, className }: { readonly label: string; readonly value: string; readonly testId: string; readonly className?: string }) {
  return (
    <div className="grid gap-0.5">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={cn('font-mono text-lg tabular-nums', className)} data-testid={testId}>
        {value}
      </span>
    </div>
  );
}

function ActivationBadge({ activation }: { readonly activation: PlayoutHeaderProps['activation'] }) {
  if (activation === 'active') return <Badge variant="onair">Active</Badge>;
  if (activation === 'rehearsal') return <Badge variant="preview">Rehearsal</Badge>;
  return <Badge variant="offline">Inactive</Badge>;
}

export function PlayoutHeader(props: PlayoutHeaderProps) {
  const countdown = formatCountdown(props.onAir?.remainingMs ?? null);
  const overUnder = formatOverUnder(props.overUnderMs);
  return (
    <header className="flex flex-wrap items-center gap-x-8 gap-y-3 border-b bg-card px-4 py-3" data-testid="playout-header">
      <div className="grid gap-1">
        <div className="flex items-center gap-2">
          <ActivationBadge activation={props.activation} />
          {props.connection === 'live' ? null : (
            <Badge variant="warning" data-testid="playout-connection" data-connection={props.connection}>
              {CONNECTION_LABEL[props.connection]}
            </Badge>
          )}
        </div>
        <span className="max-w-64 truncate text-sm text-muted-foreground">{props.productionLabel}</span>
      </div>
      <div className="grid min-w-48 gap-0.5" data-testid="onair-summary">
        <Badge variant="onair">On air</Badge>
        <span className="truncate font-medium">{props.onAir?.title ?? '–'}</span>
      </div>
      <Stat
        label={countdown.over ? 'Over' : 'Part remaining'}
        value={countdown.text}
        testId="part-countdown"
        className={countdown.over ? 'rounded bg-onair px-1 text-onair-foreground' : ''}
      />
      <Stat label="Part elapsed" value={props.onAir === null ? '–' : formatElapsed(props.onAir.elapsedMs)} testId="part-elapsed" />
      <div className="grid min-w-40 gap-0.5">
        <Badge variant="next">Next</Badge>
        <span className="truncate font-medium" data-testid="next-summary">
          {props.nextTitle ?? '–'}
        </span>
      </div>
      <Stat label="Show remaining" value={formatLength(Math.max(0, props.remainingMs))} testId="show-remaining" />
      <Stat label="Over/under" value={overUnder.text} testId="over-under" className={TONE_CLASS[overUnder.tone]} />
      <Stat label={`Expected end · ${MODE_LABEL[props.mode]}`} value={formatTimeOfDay(props.expectedEnd)} testId="expected-end" />
      <Stat label="Projected end" value={formatTimeOfDay(props.projectedEnd)} testId="projected-end" />
    </header>
  );
}
